import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import ContentCard from "../../../components/misc/ContentCard";
import useRoutesHelper from "../../../hooks/useRoutesHelper";

import {
  getScheduleWorkingPointsDetails,
  getWorkingRoutes,
  postCreateOrUpdateWorkingPoint,
} from "../../../redux/api/expedition.api";
import { resetScheduleWorkingPointsDetails } from "../../../redux/reducer/expedition.reducer";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../helpers/apiHelper";

import ConfigWorkingDays, {
  AddAllDaysItemScheduleInterface,
} from "../components/ConfigWorkingDays";
import Dropdown, { DropdownValue } from "../../../components/misc/Dropdown";
import FormButtons from "../../../components/misc/FormButtons";
import { Formik } from "formik";
import TextInput from "../../../components/misc/TextInput";
import CountriesAndLocalitiesDropdowns from "../../../components/CountriesAndLocalitiesDropdowns";
import Checkbox from "../../../components/misc/Checkbox";

interface initialValuesInterface {
  selectedWorkingRoute: DropdownValue | null;
  cod_intern: string | null;
  adresa: string;
  denumire: string;
  localitate: string;
  judet: string;
  cod_postal: string;
  pers_contact: string;
  tel_contact: string;
  nod_ats: boolean;
}

const initialValues: initialValuesInterface = {
  selectedWorkingRoute: null,
  cod_intern: "",
  adresa: "",
  denumire: "",
  judet: "-",
  localitate: "-",
  cod_postal: "",
  pers_contact: "",
  tel_contact: "",
  nod_ats: false,
};

const formikSchema = Yup.object({
  adresa: Yup.string().required("Denumirea este obligatorie"),
  denumire: Yup.string().required("Codul este obligatoriu"),
  localitate: Yup.string()
    .nullable()
    .notOneOf(["-"], "Trebuie sa alegi o localitate")
    .required("Trebuie sa alegi o localitate"),
  judet: Yup.string()
    .nullable()
    .notOneOf(["-"], "Trebuie sa alegi un judet")
    .required("Trebuie sa alegi un judet"),
  cod_postal: Yup.string().required("Codul postal este obligatoriu"),
  pers_contact: Yup.string().required("Persoana de contact este obligatorie"),
  tel_contact: Yup.string().required("Telefonul de contact este obligatoriu"),
});

function ExpeditionScheduleWorkingPointsOrganise({
  withUpdate,
}: {
  withUpdate?: boolean;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    routeParams: { id, routeId, locationName },
  } = useRoutesHelper();

  const [isSaving, setIsSaving] = useState(false);

  const [formikValues, setFormikValues] =
    useState<initialValuesInterface>(initialValues);

  const {
    expedition: {
      scheduleWorkingPointsDetails: {
        data: { days: workingPointDays, pctLucru: workingPointDetails },
        isLoading: isLoadingScheduleWorkingPointsDetails,
      },
      scheduleWorkingRoutes: { data: scheduleWorkingRoutesData, isLoading },
    },
  } = useAppSelector((state) => ({
    expedition: state.expedition,
  }));

  const [selectedDays, setSelectedDays] = useState<
    AddAllDaysItemScheduleInterface[]
  >([]);

  useEffect(() => {
    dispatch(
      getWorkingRoutes({
        page: 1,
        perPage: 500,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(getScheduleWorkingPointsDetails({ id: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    let defaultRoute = scheduleWorkingRoutesData?.data[0];

    if (defaultRoute) {
      setFormikValues((prev) => ({
        ...prev,
        selectedWorkingRoute: {
          label: defaultRoute.denumire,
          id: defaultRoute.id_ruta,
        },
      }));
    }

    if (withUpdate && routeId) {
      const selectedRoute = scheduleWorkingRoutesData.data.find(
        (route) => route.id_ruta.toString() === routeId.toString(),
      );

      if (selectedRoute) {
        defaultRoute = selectedRoute;
      }

      if (workingPointDetails) {
        setFormikValues((prev) => ({
          ...prev,
          cod_intern: workingPointDetails.cod_intern,
          adresa: workingPointDetails.adresa,
          denumire: workingPointDetails.denumire,
          judet: workingPointDetails.judet || "-",
          localitate: workingPointDetails.localitate || "-",
          cod_postal: workingPointDetails.cod_postal,
          pers_contact: workingPointDetails.pers_contact,
          tel_contact: workingPointDetails.tel_contact,
          nod_ats: !!workingPointDetails.nod_ats,
        }));
      }
    }
  }, [scheduleWorkingRoutesData, routeId, withUpdate, workingPointDetails]);

  useEffect(() => {
    return () => {
      dispatch(resetScheduleWorkingPointsDetails());
    };
  }, [dispatch]);

  const onlyActiveDays = selectedDays.filter((item) => item.isActive);

  const handleSave = async (value: initialValuesInterface) => {
    const workingRoute = scheduleWorkingRoutesData?.data.find(
      (route) => route.id_ruta === value.selectedWorkingRoute?.id,
    );

    if (!workingRoute) {
      return;
    }

    setIsSaving(true);

    const resultAction = await dispatch(
      postCreateOrUpdateWorkingPoint({
        ...value,
        activeWorkingDays: onlyActiveDays,
        id: id,
        selectedWorkingRoute: workingRoute,
      }),
    );

    if (
      checkEndpointStatus(resultAction, postCreateOrUpdateWorkingPoint) !==
      endpointStatus.pending
    ) {
      setIsSaving(false);
    }

    if (
      checkEndpointStatus(resultAction, postCreateOrUpdateWorkingPoint) ===
      endpointStatus.fulfilled
    ) {
      navigate(-1);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const scheduleWorkingRoutesDataFormatted =
    scheduleWorkingRoutesData?.data.map((route) => ({
      id: route.id_ruta,
      label: route.denumire,
    }));

  return (
    <ContentCard
      isLoading={isLoading || isLoadingScheduleWorkingPointsDetails}
      cardTitle={
        withUpdate
          ? `Organizare locatie de livrare ${locationName}`
          : "Creeaza locatie de livrare"
      }>
      <Formik
        enableReinitialize
        initialValues={formikValues}
        validationSchema={formikSchema}
        onSubmit={handleSave}>
        {({
          errors,
          touched,
          values,
          isSubmitting,
          getFieldProps,
          handleSubmit,
          setFieldValue,
        }) => {
          const { selectedWorkingRoute, nod_ats } = values;

          return (
            <form
              className="form w-100"
              onSubmit={handleSubmit}
              noValidate
              id="kt_login_signin_form">
              <TextInput
                label="Cod intern"
                name="cod_intern"
                error={errors["cod_intern"]}
                touched={touched["cod_intern"]}
                inputProps={getFieldProps("cod_intern")}
              />
              <TextInput
                label="Adresa"
                name="adresa"
                error={errors["adresa"]}
                touched={touched["adresa"]}
                inputProps={getFieldProps("adresa")}
              />
              <TextInput
                label="Punct de livrare"
                name="denumire"
                error={errors["denumire"]}
                touched={touched["denumire"]}
                inputProps={getFieldProps("denumire")}
              />
              <CountriesAndLocalitiesDropdowns
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
              />
              <TextInput
                label="Cod postal"
                name="cod_postal"
                error={errors["cod_postal"]}
                touched={touched["cod_postal"]}
                inputProps={getFieldProps("cod_postal")}
              />
              <TextInput
                label="Persoana de contact"
                name="pers_contact"
                error={errors["pers_contact"]}
                touched={touched["pers_contact"]}
                inputProps={getFieldProps("pers_contact")}
              />
              <TextInput
                type="tel"
                label="Telefon de contact"
                name="tel_contact"
                error={errors["tel_contact"]}
                touched={touched["tel_contact"]}
                inputProps={getFieldProps("tel_contact")}
              />

              <Dropdown
                className="mb-4 me-2"
                title="Selecteaza ruta"
                data={scheduleWorkingRoutesDataFormatted}
                singleValue={selectedWorkingRoute}
                setSingleValue={(value) => {
                  setFieldValue("selectedWorkingRoute", value);
                }}
              />

              <div className="d-flex mb-10">
                <Checkbox
                  name="nod_ats"
                  label="Nod-ATS"
                  checked={nod_ats}
                  onChange={(value) => setFieldValue("nod_ats", value)}
                />
              </div>

              <ConfigWorkingDays
                withoutTime
                alreadyCheckedDays={workingPointDays}
                setSelectedDays={setSelectedDays}
              />

              <FormButtons
                goBackLabel="Anuleaza"
                goNextLabel="Salveaza"
                handleGoBack={handleCancel}
                nextButtonDisabled={isSubmitting || onlyActiveDays.length === 0}
                nextButtonLoading={isSaving}
              />
            </form>
          );
        }}
      </Formik>
    </ContentCard>
  );
}

export default ExpeditionScheduleWorkingPointsOrganise;
